import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import Cropper from "react-easy-crop";
import Api from "../../services/Api";
import { useResponsive } from "../../hooks/useResponsive";

/* =========================
   CROP UTILS
========================= */

async function getCroppedImg(imageSrc, croppedAreaPixels) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  const size = 300;
  canvas.width = size;
  canvas.height = size;

  ctx.beginPath();
  ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
  ctx.closePath();
  ctx.clip();

  ctx.drawImage(
    image,
    croppedAreaPixels.x,
    croppedAreaPixels.y,
    croppedAreaPixels.width,
    croppedAreaPixels.height,
    0,
    0,
    size,
    size,
  );

  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      resolve({
        blob,
        preview: URL.createObjectURL(blob),
      });
    }, "image/jpeg");
  });
}

function createImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.crossOrigin = "anonymous";
    img.src = url;
  });
}

/* =========================
   COMPONENT
========================= */

export default function MessageModal({ isOpen, onClose, falecidoId }) {
  const { isMobile } = useResponsive();

  const [form, setForm] = useState({
    mensagem: "",
    solicitante: "",
    parentesco: "",
    telefone: "",
    historia: "",
    preview: null,
    file: null,
    video: null,
    videoPreview: null,
  });

  const [imageSrc, setImageSrc] = useState(null);
  const [showCrop, setShowCrop] = useState(false);

  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const [openSections, setOpenSections] = useState({
    historia: false,
    video: false,
  });

  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [videoError, setVideoError] = useState(null);

  useEffect(() => {
    document.body.style.overflow = showCrop ? "hidden" : "auto";
    return () => (document.body.style.overflow = "auto");
  }, [showCrop]);

  if (!isOpen) return null;

  /* =========================
     HANDLERS
  ========================= */

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const toggleSection = (key) => {
    setOpenSections((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleImage = (file) => {
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      setImageSrc(reader.result);
      setShowCrop(true);
    };
    reader.readAsDataURL(file);
  };

  const handleVideoUpload = (file) => {
    if (!file) return;

    setVideoError(null);

    const maxSizeMB = 200;
    if (file.size > maxSizeMB * 1024 * 1024) {
      setVideoError("Vídeo muito grande (máx 200MB)");
      return;
    }

    const url = URL.createObjectURL(file);

    setForm((prev) => ({
      ...prev,
      video: file,
      videoPreview: url,
    }));

    setUploading(true);
    setUploadProgress(0);

    const interval = setInterval(() => {
      setUploadProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          setUploading(false);
          return 100;
        }
        return prev + 10;
      });
    }, 120);
  };

  const removeVideo = () => {
    setForm((prev) => ({
      ...prev,
      video: null,
      videoPreview: null,
    }));

    setUploadProgress(0);
    setUploading(false);
    setVideoError(null);
  };

  const onCropComplete = (_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  };

  const handleCropSave = async () => {
    const { blob, preview } = await getCroppedImg(imageSrc, croppedAreaPixels);

    setForm((prev) => ({
      ...prev,
      preview,
      file: blob,
    }));

    setShowCrop(false);
  };

  const handleSubmit = () => {
    if (!form.mensagem || !form.solicitante || !form.parentesco || !form.telefone || !form.file) {
      alert("Preencha todos os campos obrigatórios");
      return;
    }

    const data = new FormData();

    data.append("file", form.file, "imagem.jpg");
    data.append("mensagem", form.mensagem);
    data.append("solicitante", form.solicitante);
    data.append("parentesco", form.solicitante + " - " + form.parentesco);
    data.append("telefone", form.telefone);
    data.append("historia", form.historia);
    data.append("falecido_id", falecidoId);

    if (form.video) {
      data.append("video", form.video, form.video.name);
    }

    setUploading(true);
    setUploadProgress(0);

    Api.uploadMessage(data, (percent) => {
      setUploadProgress(percent);
    })
      .then(() => {
        setUploadProgress(100);
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        setUploading(false);
      });

    onClose();
  };

  const boxSize = isMobile ? 180 : 220;

  const SectionHeader = ({ title, sectionKey }) => (
    <div onClick={() => toggleSection(sectionKey)} style={sectionHeader}>
      <span>{title}</span>
      <span>{openSections[sectionKey] ? "▲" : "▼"}</span>
    </div>
  );

  return createPortal(
    <>
      {/* MODAL */}
      <div style={overlay}>
        <div style={{ ...modal, width: isMobile ? "95vw" : 500 }}>
          <div style={header}>
            <h3 style={{ margin: 0 }}>Enviar Homenagem</h3>
            <button onClick={onClose} style={closeButton}>
              ✕
            </button>
          </div>

          <div style={body}>
            {/* IMAGEM */}
            <div style={field}>
              <label style={label}>Imagem</label>

              <div style={imageWrapper}>
                <div style={{ ...uploadBox, width: boxSize, height: boxSize }}>
                  {form.preview ? (
                    <img
                      src={form.preview}
                      alt="Preview da imagem enviada"
                      style={previewImg}
                    />
                  ) : (
                    <span style={placeholder}>
                      Clique para adicionar imagem
                    </span>
                  )}

                  <input
                    type="file"
                    accept="image/*"
                    style={fileInput}
                    onChange={(e) => handleImage(e.target.files[0])}
                  />
                </div>
              </div>
            </div>

            {/* TEXTO */}
            <div style={field}>
              <label style={label}>Mensagem</label>
              <textarea
                value={form.mensagem}
                maxLength={500}
                onChange={(e) => handleChange("mensagem", e.target.value)}
                style={textarea}
              />
            </div>

            <div style={field}>
              <label style={label}>Solicitante</label>
              <input
                value={form.solicitante}
                onChange={(e) => handleChange("solicitante", e.target.value)}
                style={input}
              />
            </div>

            <div style={field}>
              <label style={label}>Parentesco</label>
              <select
                value={form.parentesco}
                onChange={(e) => handleChange("parentesco", e.target.value)}
                style={input}
              >
                <option value="">Selecione</option>
                <option>Pai</option>
                <option>Mãe</option>
                <option>Filho(a)</option>
                <option>Irmão(ã)</option>
                <option>Amigo(a)</option>
              </select>
            </div>

            <div style={field}>
              <label style={label}>Telefone</label>
              <input
                value={form.telefone}
                onChange={(e) => handleChange("telefone", e.target.value)}
                style={input}
              />
            </div>

            {/* HISTÓRIA */}
            <SectionHeader title="História (opcional)" sectionKey="historia" />

            {openSections.historia && (
              <div style={field}>
                <textarea
                  value={form.historia}
                  maxLength={2000}
                  onChange={(e) => handleChange("historia", e.target.value)}
                  style={{ ...textarea, height: 160 }}
                />
              </div>
            )}

            {/* VÍDEO */}
            <SectionHeader title="Vídeo (opcional)" sectionKey="video" />

            {openSections.video && (
              <div style={field}>
                {!form.videoPreview ? (
                  <label
                    style={{
                      ...videoDropzone,
                      borderColor: dragActive ? "#E8956A" : "#ccc",
                      background: dragActive ? "#fff6f1" : "#fafafa",
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setDragActive(true);
                    }}
                    onDragLeave={() => setDragActive(false)}
                    onDrop={(e) => {
                      e.preventDefault();
                      handleVideoUpload(e.dataTransfer.files[0]);
                    }}
                  >
                    <input
                      type="file"
                      accept="video/*"
                      style={{ display: "none" }}
                      onChange={(e) => handleVideoUpload(e.target.files[0])}
                    />
                    Clique ou arraste vídeo
                  </label>
                ) : (
                  <div style={videoCard}>
                    <video src={form.videoPreview} controls style={video} />

                    {uploading && (
                      <div style={progressBar}>
                        <div
                          style={{
                            ...progressFill,
                            width: `${uploadProgress}%`,
                          }}
                        />
                      </div>
                    )}

                    <div style={videoActions}>
                      <label style={btnPrimary}>
                        Trocar
                        <input
                          type="file"
                          accept="video/*"
                          style={{ display: "none" }}
                          onChange={(e) => handleVideoUpload(e.target.files[0])}
                        />
                      </label>

                      <button onClick={removeVideo} style={btnSecondary}>
                        Remover
                      </button>
                    </div>
                  </div>
                )}

                {videoError && <div style={error}>{videoError}</div>}
              </div>
            )}
          </div>

          <div style={footer}>
            {uploading && (
              <div style={{ width: "100%" }}>
                <div style={progressBar}>
                  <div
                    style={{
                      ...progressFill,
                      width: `${uploadProgress}%`,
                    }}
                  />
                </div>

                <div style={{ textAlign: "center", marginTop: 5 }}>
                  {uploadProgress}%
                </div>
              </div>
            )}

            <button onClick={onClose} style={cancel}>
              Cancelar
            </button>

            <button onClick={handleSubmit} style={send}>
              Enviar
            </button>
          </div>
        </div>
      </div>

      {/* CROP */}
      {showCrop && (
        <div style={cropOverlay}>
          <div style={cropBox}>
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={1}
              cropShape="round"
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />

            <div style={cropControls}>
              <input
                type="range"
                min={1}
                max={3}
                step={0.01}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
              />
              <button onClick={handleCropSave}>OK</button>
              <button onClick={() => setShowCrop(false)}>Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </>,
    document.body,
  );
}

/* =========================
   STYLES (SaaS)
========================= */

const overlay = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,.4)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 9999,
};
const modal = { background: "#fff", borderRadius: 12, overflow: "hidden" };
const header = {
  background: "#E8956A",
  padding: 12,
  color: "#fff",
  display: "flex",
  justifyContent: "space-between",
};
const closeButton = { background: "transparent", border: 0, color: "#fff" };
const body = { padding: 20, maxHeight: "70vh", overflowY: "auto" };

const field = { marginBottom: 15, display: "flex", flexDirection: "column" };
const label = { fontWeight: "bold" };
const input = { padding: 8, borderRadius: 6, border: "1px solid #ccc" };
const textarea = { ...input, resize: "none" };

const imageWrapper = { display: "flex", justifyContent: "center" };
const uploadBox = {
  width: 220,
  height: 220,
  borderRadius: "50%",
  border: "2px dashed #ccc",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  overflow: "hidden",
  position: "relative",
  cursor: "pointer",
};
const previewImg = { width: "100%", height: "100%", objectFit: "cover" };
const fileInput = {
  position: "absolute",
  width: "100%",
  height: "100%",
  opacity: 0,
};

const placeholder = { fontSize: 12, color: "#999", textAlign: "center" };

const sectionHeader = {
  display: "flex",
  justifyContent: "space-between",
  padding: "10px 0",
  borderBottom: "1px solid #eee",
  cursor: "pointer",
};

const videoDropzone = {
  height: 160,
  border: "2px dashed #ccc",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: 12,
};
const videoCard = {
  border: "1px solid #eee",
  borderRadius: 12,
  overflow: "hidden",
};
const video = { width: "100%" };

const progressBar = { height: 4, background: "#eee" };
const progressFill = { height: 4, background: "#E8956A" };

const videoActions = {
  display: "flex",
  justifyContent: "space-between",
  padding: 10,
};
const btnPrimary = {
  background: "#E8956A",
  color: "#fff",
  padding: "6px 10px",
  borderRadius: 6,
  fontSize: 12,
};
const btnSecondary = {
  background: "#ddd",
  padding: "6px 10px",
  borderRadius: 6,
  fontSize: 12,
};

const error = { color: "red", fontSize: 12, marginTop: 5 };

const footer = {
  display: "flex",
  justifyContent: "flex-end",
  gap: 10,
  padding: 15,
};
const cancel = { background: "#ccc", border: 0, padding: "8px 12px" };
const send = {
  background: "#E8956A",
  border: 0,
  padding: "8px 12px",
  color: "#fff",
};

const cropOverlay = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,.7)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 10000,
};
const cropBox = {
  width: "min(90vw,400px)",
  height: "min(90vw,400px)",
  background: "#000",
  position: "relative",
};
const cropControls = {
  position: "absolute",
  bottom: 0,
  width: "100%",
  background: "rgba(0,0,0,.6)",
  display: "flex",
  gap: 10,
  justifyContent: "center",
  padding: 10,
};
